import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { formatMinor } from '../../../shared/utils/money';
import { useCharterBookings } from '../hooks/useCharter';
import { BookingDetailModal } from '../components/BookingDetailModal';
import { BookingStatusBadge, PaymentStatusBadge } from '../components/CharterStatusBadge';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { formatWat } from '../components/charterFormat';

export function CharterBookingsPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const { data: bookings = [], isLoading, error } = useCharterBookings(selectedTenantId);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Bookings"
        subtitle="Service bookings for this provider. Open one to view intake responses, referenced work, and take action."
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter bookings" />
      ) : isLoading ? (
        <LoadingState label="Loading bookings…" />
      ) : error ? (
        <ErrorState label="Failed to load bookings." />
      ) : bookings.length === 0 ? (
        <EmptyState label="No bookings yet." />
      ) : (
        <TableCard>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className={TH}>Customer</th>
                <th className={TH}>Status</th>
                <th className={TH}>Payment</th>
                <th className={TH}>Slot (WAT)</th>
                <th className={TH}>Balance due</th>
                <th className={TH}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {bookings.map((b) => (
                <tr key={b.id} className="hover:bg-muted transition-colors">
                  <td className={TD}>
                    <span className="font-medium">{b.customer?.name ?? '—'}</span>
                    <span className="block text-xs text-muted-foreground">{b.customer?.email}</span>
                  </td>
                  <td className="px-6 py-4">
                    <BookingStatusBadge status={b.status} />
                  </td>
                  <td className="px-6 py-4">
                    <PaymentStatusBadge status={b.paymentStatus} />
                  </td>
                  <td className={TD_MUTED}>{b.slot ? formatWat(b.slot.startsAt) : 'Unscheduled'}</td>
                  <td className={TD_MUTED}>
                    {b.remainingBalance ? formatMinor(b.remainingBalance, b.currency) : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => setOpenId(b.id)} className="text-xs text-blue-600 hover:underline">
                      {canManage ? 'Manage' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      {openId && (
        <BookingDetailModal
          bookingId={openId}
          tenantId={selectedTenantId}
          canManage={canManage}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
